import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apicallApi } from '../../../api/apicall';
import Navbar from '../../layout/Navbar';

function EditApiCall() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    name: '',
    method: 'GET',
    url: '',
    body: '',
    position: 0
  });
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchApiCall = async () => {
      try {
        setLoading(true);
        const data = await apicallApi.getApiCallById(id);
        
        // Format the body as a string if it exists
        const bodyString = data.body ? JSON.stringify(data.body, null, 2) : '';
        
        setFormData({
          name: data.name || '',
          method: data.method || 'GET',
          url: data.url || '',
          body: bodyString,
          position: data.position || 0
        });
        
        setLoading(false);
      } catch (err) {
        setError(err.message || 'Failed to fetch API call');
        setLoading(false);
      }
    };

    if (id) {
      fetchApiCall();
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when field changes
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleBodyChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      body: value
    }));
    
    // Clear JSON validation error when body changes
    if (validationErrors.body) {
      setValidationErrors(prev => ({
        ...prev,
        body: null
      }));
    }
  };

  const validateJsonBody = () => {
    // Only validate if there is content and the method is not GET
    if (formData.body.trim() && formData.method !== 'GET') {
      try {
        JSON.parse(formData.body);
        return true;
      } catch (err) {
        setValidationErrors(prev => ({
          ...prev,
          body: 'Invalid JSON format'
        }));
        return false;
      }
    }
    return true; // No body or GET request is fine
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.url.trim()) {
      errors.url = 'URL is required';
    } else if (!/^https?:\/\/.+/.test(formData.url) && !formData.url.includes('{')) {
      errors.url = 'URL must start with http:// or https:// (unless using variables)';
    }
    
    // Set all validation errors at once
    setValidationErrors(errors);
    
    // Return true if no errors and JSON is valid
    return Object.keys(errors).length === 0 && validateJsonBody();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Prepare request body
      const apiCallData = {
        ...formData,
        body: formData.body.trim() ? JSON.parse(formData.body) : {}
      };
      
      // If it's a GET request, we don't need to send a body
      if (formData.method === 'GET') {
        delete apiCallData.body;
      }
      
      await apicallApi.updateApiCall(id, apiCallData);
      navigate('/api-calls');
    } catch (err) {
      setError(err.message || 'Failed to update API call');
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="mt-2 text-gray-600">Loading API call...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-2 mb-6">
          <button 
            onClick={() => navigate('/api-calls')}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to API Calls
          </button>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-6">Edit API Call</h1>

        <div className="bg-white rounded-lg shadow-md p-6">
          {error && (
            <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
              <p>{error}</p>
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full p-2 rounded border ${validationErrors.name ? 'border-red-500' : 'border-gray-300'} focus:border-black focus:outline-none`}
                  placeholder="My API Call"
                />
                {validationErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                )}
              </div>

              <div>
                <label htmlFor="method" className="block text-sm font-medium text-gray-700 mb-2">
                  HTTP Method
                </label>
                <select
                  id="method"
                  name="method"
                  value={formData.method}
                  onChange={handleChange}
                  className="w-full p-2 rounded border border-gray-300 focus:border-black focus:outline-none"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                  <option value="PATCH">PATCH</option>
                </select>
              </div>

              <div>
                <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-2">
                  URL Template *
                </label>
                <input
                  type="text"
                  id="url"
                  name="url"
                  value={formData.url}
                  onChange={handleChange}
                  className={`w-full p-2 rounded border ${validationErrors.url ? 'border-red-500' : 'border-gray-300'} focus:border-black focus:outline-none`}
                  placeholder="https://api.example.com/endpoint"
                />
                {validationErrors.url && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.url}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  You can use {'{parameter}'} syntax for dynamic values
                </p>
              </div>

              <div>
                <label htmlFor="body" className="block text-sm font-medium text-gray-700 mb-2">
                  Request Body Template (JSON)
                </label>
                <textarea
                  id="body"
                  name="body"
                  value={formData.body}
                  onChange={handleBodyChange}
                  rows="8"
                  className={`w-full p-2 rounded border ${validationErrors.body ? 'border-red-500' : 'border-gray-300'} focus:border-black focus:outline-none font-mono text-sm`}
                  placeholder={formData.method !== 'GET' ? '{\n  "key": "value",\n  "dynamic": "{parameter}"\n}' : 'Not applicable for GET requests'}
                  disabled={formData.method === 'GET'}
                />
                {validationErrors.body && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.body}</p>
                )}
                {formData.method === 'GET' && (
                  <p className="mt-1 text-sm text-gray-500">GET requests don't have a request body.</p>
                )}
                {formData.method !== 'GET' && (
                  <p className="mt-1 text-xs text-gray-500">
                    You can use {'{parameter}'} syntax for dynamic values in the JSON
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-2">
                  Position
                </label>
                <input
                  type="number"
                  id="position"
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  min="0"
                  className="w-full p-2 rounded border border-gray-300 focus:border-black focus:outline-none"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Order in which this API call will be executed (0 = first)
                </p>
              </div>

              <div className="flex justify-end gap-4 mt-4">
                <button
                  type="button"
                  onClick={() => navigate('/api-calls')}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-4 py-2 bg-black hover:bg-gray-800 text-white rounded-lg transition-colors ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? 'Updating...' : 'Update API Call'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditApiCall; 